/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, ActionButton, CheckIconButton } from '@components/atoms'
import { UserList, SideModal, Tabs, Tab } from '@components/molecules'
import { AccessGroupEditor, AccessGroupList } from '@components/systems'

import {
	useAppSelector,
	useAppDispatch,
	SecetionUserListType,
	accessControlActions,
	ApiGetDataStatus,
	AccessControlStateType,
	CrudType,
} from '@redux/index'
import { grey, blue } from '@mui/material/colors'
import { emptyGroup } from '@redux/store/admin/accessControl.store'
import { UserType, AccessGroupType } from '@schemas'
import { produce } from 'immer'

export const AccessControlSideModal = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const crud: CrudType = useAppSelector(state => state.auth.currentAccess)
	const leftModalInfo: AccessControlStateType['leftModalInfo'] = useAppSelector(
		state => state.accessControl.leftModalInfo,
	)
	const groupList: AccessGroupType[] = useAppSelector(
		state => state.accessControl.groupList,
	)
	const userList: UserType[] = useAppSelector(state => state.accessControl.userList)
	const groupListStatus: ApiGetDataStatus = useAppSelector(
		state => state.accessControl.groupListStatus,
	)
	const userListStatus: ApiGetDataStatus = useAppSelector(
		state => state.accessControl.userListStatus,
	)
	const userSectionList: SecetionUserListType[] = useAppSelector(
		state => state.accessControl.userSectionList,
	)
	const selectedAccessUser: UserType = useAppSelector(
		state => state.accessControl.selectedAccessUser,
	)
	const selectedAccessGroup: AccessGroupType = useAppSelector(
		state => state.accessControl.selectedAccessGroup,
	)
	const showSideModal: boolean = useAppSelector(
		state => state.accessControl.showSideModal,
	)
	const leftModalTabIndex: number = useAppSelector(
		state => state.accessControl.leftModalTabIndex,
	)
	const groupListByUserTab: AccessGroupType[] = useAppSelector(
		state => state.accessControl.groupListByUserTab,
	)

	// state
	const [openedAddUserCateg, setOpenedAddUserCateg] = React.useState<string[]>([])
	const [selectedAddUserIds, setSelectedAddUserIds] = React.useState<string[]>([])
	const [isChanged, setIsChanged] = React.useState<boolean>(false)

	// variables
	const title: string =
		leftModalInfo.target === 'groupEditor' ? selectedAccessGroup.name : '그룹 리스트'

	React.useEffect(() => {
		const newSelectedUserIds = selectedAccessGroup.users.map(aUser => aUser.id)
		setSelectedAddUserIds(newSelectedUserIds)
	}, [selectedAccessGroup])

	React.useEffect(() => {
		const prevUserIds: string[] = selectedAccessGroup['users'].map(aUser => aUser.id)
		const isNotChangedForPrev: boolean = prevUserIds.every(aPrevId =>
			selectedAddUserIds.includes(aPrevId),
		)
		const isNotChangedForNew: boolean = selectedAddUserIds.every(aNewId =>
			prevUserIds.includes(aNewId),
		)

		setIsChanged(!(isNotChangedForPrev && isNotChangedForNew))
	}, [selectedAddUserIds, selectedAccessGroup['users']])

	const leftModalTabList = React.useMemo(
		() => [
			{
				index: 0,
				id: 'title',
				label: '이름 변경',
			},
			{
				index: 1,
				id: 'member',
				label: '그룹 멤버 관리',
			},
		],
		[],
	)

	const onTabIndexChange = React.useCallback(
		(e: React.SyntheticEvent, newIndex: number) => {
			reduxDispatch(accessControlActions.LEFT_MODAL_TAB_INDEX(newIndex))
		},
		[],
	)

	const onGroupDelete = React.useCallback(() => {
		reduxDispatch(accessControlActions.DELETE_GROUP(selectedAccessGroup.id))
		reduxDispatch(accessControlActions.SELECTED_ACCESS_GROUP(emptyGroup))
		reduxDispatch(accessControlActions.SHOW_SIDE_MODAL(false))
	}, [selectedAccessGroup, emptyGroup])

	const onAddUserSave = React.useCallback(() => {
		reduxDispatch(
			accessControlActions.UPDATE_ACCESS_GROUP_USERS({
				accessGroupId: selectedAccessGroup.id,
				userIds: selectedAddUserIds,
			}),
		)
	}, [selectedAddUserIds])

	const onUserClick = (aUser: UserType) => {
		const newUserIds = [...selectedAddUserIds]
		const index = newUserIds.indexOf(aUser.id)
		if (index > -1) {
			// delete
			newUserIds.splice(index, 1)
		} else {
			// add
			newUserIds.push(aUser.id)
		}

		setSelectedAddUserIds(newUserIds)
	}

	const onAddUserCollapsOpen = (id: string) => {
		const newOpenedAddUserCateg: string[] = produce(openedAddUserCateg, draft => {
			const idx: number = openedAddUserCateg.indexOf(id)
			if (idx === -1) {
				draft.push(id)
			} else {
				draft.splice(idx, 1)
			}
		})

		setOpenedAddUserCateg(newOpenedAddUserCateg)
	}

	const onGroupClick = (aGroup: AccessGroupType) => {
		const thisGroup = groupList.filter(aGroup2 => aGroup2.id === aGroup.id)[0]
		const newUserId = selectedAccessUser.id
		const newUserIds = thisGroup.users.map(aUser => aUser.id)
		const index = thisGroup.users.findIndex(aUser => aUser.id === newUserId)

		if (index > -1) {
			// delete
			newUserIds.splice(index, 1)
		} else {
			// add
			newUserIds.push(newUserId)
		}

		reduxDispatch(
			accessControlActions.UPDATE_ACCESS_GROUP_USERS({
				accessGroupId: thisGroup.id,
				userIds: newUserIds,
			}),
		)
	}

	return (
		<View css={style}>
			<SideModal
				show={showSideModal}
				title={selectedAccessGroup.name}
				renderTitle={<Text i18n={selectedAccessGroup.i18n}>{title}</Text>}
				onCloseModal={() => reduxDispatch(accessControlActions.SHOW_SIDE_MODAL(false))}
			>
				<View className="accessControlSideModalContent">
					{leftModalInfo.target === 'groupEditor' ? (
						<>
							<View className="leftModlaTabList">
								<Tabs
									value={leftModalTabIndex}
									indicatorColor="primary"
									textColor="primary"
									onChange={onTabIndexChange}
									aria-label="disabled tabs example"
								>
									{leftModalTabList.map(aTab => (
										<Tab key={aTab.id} label={aTab.label} className="tabItem" />
									))}
								</Tabs>
							</View>

							{leftModalTabIndex === 0 ? (
								<View>
									<AccessGroupEditor show={showSideModal} onDelete={onGroupDelete} />
								</View>
							) : leftModalTabIndex === 1 ? (
								<View className="accessGroupAddUserListOuter">
									<View className="accessGroupAddUserListView">
										<UserList
											status={userListStatus}
											userList={userList}
											onUserClick={
												crud.includes('U')
													? (aUser: UserType) => onUserClick(aUser)
													: undefined
											}
											openedUserCateg={openedAddUserCateg}
											onCollapsOpen={onAddUserCollapsOpen}
											selectedUserIds={selectedAddUserIds}
										/>
									</View>

									<View className="accessGroupAddUserListBottomView">
										<Text className="accessGroupAddedUserCount">{`${selectedAddUserIds.length}명 선택됨`}</Text>

										{crud.includes('U') && (
											<View className="addButtonView">
												<ActionButton
													title="저장"
													tooltip="저장"
													style={{ margin: 0 }}
													buttonColor="primary"
													onClick={onAddUserSave}
													iconName="check-bold"
													disabled={!isChanged}
													iconTextColor={blue['A400']}
												/>
											</View>
										)}
									</View>
								</View>
							) : null}
						</>
					) : (
						<View className="joinGroupListView">
							<AccessGroupList
								groupList={groupList}
								status={groupListStatus}
								onGroupClick={onGroupClick}
								selectedGroupIds={groupListByUserTab.map(aGroup => aGroup.id)}
								rightAccessory={(group, isChecked) => {
									return (
										<CheckIconButton
											checked={isChecked}
											css={{ marginRight: '1rem' }}
											onClick={() => onGroupClick(group)}
										/>
									)
								}}
							/>
						</View>
					)}
				</View>
			</SideModal>
		</View>
	)
}

const style = css({
	'.accessControlSideModalContent': {
		height: 'calc(100vh - 5.8rem)',

		'.leftModlaTabList': {
			padding: '0 0.5rem',

			'.MuiTabs-root': {
				minHeight: '2rem',
				height: '2rem',

				'.MuiTab-root': {
					minHeight: '2rem',
					height: '2rem',
				},
			},
		},

		'.accessGroupAddUserListOuter': {
			height: 'calc(100% - 2rem)',

			'.accessGroupAddUserListView': {
				height: 'calc(100% - 3rem)',
				padding: '0.5rem 1rem',
				overflow: 'auto',
			},
		},
	},

	'.accessGroupAddUserListBottomView': {
		position: 'absolute',
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'center',
		width: '100%',
		height: '3rem',
		bottom: 0,
		padding: '0 0.5rem',
		background: blue['A400'],
		borderTop: '1px solid',
		borderColor: grey[200],

		'.accessGroupAddedUserCount': {
			position: 'absolute',
			width: 'fit-content',
			top: '0.2rem',
			bottom: 0,
			left: '0.5rem',
			maring: 'auto',
			color: '#FFF',
		},

		'.addButtonView': {
			background: '#FFF',
			borderRadius: '5px',
		},
	},

	'.joinGroupListView': {
		width: '100%',
		height: '100%',
	},
})
